import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";
import { NotificationDot } from "./notification-dot";

/**
 * Квадратная иконочная кнопка: 48px (шапка, боковая навигация), 40px («ещё»
 * у карточки и мобильная шапка), 24px — строчная. Радиус 10 у крупных, 6 у 24.
 * Серая грань — стекло: за ней backdrop-blur(150px), а не плоская заливка.
 */
const squareBtnVariants = cva(
	[
		"relative inline-flex shrink-0 items-center justify-center text-fg",
		"focus-ring",
		"disabled:cursor-not-allowed disabled:bg-btn-disabled disabled:text-fg-faint",
	],
	{
		variants: {
			size: {
				24: "iec-press-sm size-6 rounded-sm",
				40: "iec-press size-10 rounded-control",
				48: "iec-press size-12 rounded-control",
			},
			variant: {
				// Светлая серая `#f1f2f4`, не `#fffffb`: квадраты шапки иначе сливаются с карточкой.
				second: "bg-btn-secondary backdrop-blur-[150px] not-disabled:hover:bg-btn-secondary-hover",
				primary: "bg-brand not-disabled:hover:bg-brand-hover",
				dark: "bg-ink text-warm",
				ghost: "bg-transparent not-disabled:hover:bg-black/4",
			},
			onGrey: { true: "", false: "" },
		},
		compoundVariants: [
			// Иконки на белой шапке: серая плашка, ховер затемняет. Не путать с second
			// без onGrey — там холодная кнопочная серая, чтобы белый квадрат не растворялся.
			{ variant: "second", onGrey: true, class: "bg-sunken not-disabled:hover:bg-sunken-hover" },
		],
		defaultVariants: { size: 48, variant: "second", onGrey: false },
	},
);

export interface SquareBtnProps
	extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled">,
		VariantProps<typeof squareBtnVariants> {
	state?: "default" | "hover" | "active" | "disabled";
	icon?: ReactNode;
	notification?: number | string;
	/** Обязательна: кнопка без подписи нечитаема скринридером. */
	"aria-label": string;
}

export function SquareBtn({
	size,
	variant,
	onGrey,
	state = "default",
	icon,
	notification,
	className,
	children,
	type = "button",
	...rest
}: SquareBtnProps) {
	const disabled = state === "disabled";
	const forcedHover = state === "hover" || state === "active";
	// Бейдж на 48px сидит по внутреннему верхнему правому углу глифа (DS: 27/9),
	// на мелких — по внешнему, иначе он перекрывает саму иконку.
	const badgePosition =
		size === 24 ? "-top-1 -right-1" : size === 40 ? "top-1.5 left-[22px]" : "top-[9px] left-[27px]";

	return (
		<button
			type={type}
			disabled={disabled}
			className={cn(
				squareBtnVariants({ size, variant, onGrey }),
				forcedHover && variant === "primary" && "bg-brand-hover",
				forcedHover &&
					(variant === "second" || variant == null) &&
					(onGrey ? "bg-sunken-hover" : "bg-btn-secondary-hover"),
				forcedHover && variant === "ghost" && "bg-black/4",
				className,
			)}
			{...rest}
		>
			{icon ?? children}
			{notification != null ? (
				<NotificationDot
					value={notification}
					className={cn("absolute", badgePosition)}
					ringClassName={onGrey ? "ring-sunken" : "ring-card"}
				/>
			) : null}
		</button>
	);
}

export { squareBtnVariants };
